<template>
  <b-tabs v-model="activeTab" class="user-list-tab" :vertical="true">
    <b-tab-item>
      <template #header>
        <!-- <b-icon icon="account-check" /> -->
        <span>
          <b-tag type="is-primary" rounded> {{ users.length }} </b-tag>
          {{ $t('users.approved') }}</span
        >
      </template>
    </b-tab-item>
    <b-tab-item>
      <template #header>
        <!-- <b-icon icon="account-clock" /> -->
        <span>
          <b-tag type="is-primary" rounded>
            {{ usersPendingApproval.length }}
          </b-tag>
          {{ $t('users.pending_approval') }}</span
        >
      </template>
    </b-tab-item>
    <b-tab-item>
      <template #header>
        <!-- <b-icon icon="login" /> -->
        <span
          ><b-tag type="is-primary" rounded>
            {{ usersLoggedInToday.length }}
          </b-tag>
          {{ $t('users.todays_logins') }}</span
        >
      </template>
    </b-tab-item>
    <b-tab-item v-if="isCurrentUserPowerAdmin">
      <template #header>
        <!-- <b-icon icon="account-cancel" /> -->
        <span
          ><b-tag type="is-primary" rounded>
            {{ usersRejected.length }}
          </b-tag>
          {{ $t('users.rejected') }}
        </span>
      </template>
    </b-tab-item>
    <b-tab-item>
      <template #header>
        <span
          ><b-tag type="is-primary" rounded>
            {{ usersWithoutSepiaInfo.length }}
          </b-tag>
          {{ $t('users.inactive') }}
        </span>
      </template>
    </b-tab-item>

    <div class="user-list">
      <template v-if="activeTab === 0">
        <p>{{ $t('users.approved_description') }}</p>
        <user-list :users="users" @selected="editUserProfile($event)" />
      </template>
      <template v-else-if="activeTab === 1">
        <p>{{ $t('users.pending_approval_description') }}</p>
        <user-list
          :users="usersPendingApproval"
          @selected="editUserProfile($event)"
        />
      </template>
      <template v-else-if="activeTab === 2">
        <p>{{ $t('users.todays_logins_description') }}</p>
        <user-list
          :users="usersLoggedInToday"
          @selected="editUserProfile($event)"
        />
      </template>
      <template v-else-if="activeTab === 3">
        <p>{{ $t('users.rejected_description') }}</p>
        <user-list :users="usersRejected" @selected="editUserProfile($event)" />
      </template>
      <template v-else-if="activeTab === 4">
        <p>{{ $t('users.inactive_description') }}</p>
        <user-list
          :users="usersWithoutSepiaInfo"
          @selected="editUserProfile($event)"
        />
      </template>
    </div>
  </b-tabs>
</template>

<script>
import { debounce } from 'lodash';
import {
  USERS_GET_USERS,
  USERS_GET_USERS_PENDING_APPROVAL,
  USERS_GET_USERS_LOGGED_IN_TODAY,
  USERS_GET_USERS_REJECTED,
  USERS_GET_USERS_WITHOUT_SEPIA_INFO,
} from '../../store/users/actionTypes';
import {
  SET_USERS,
  SET_USERS_PENDING_APPROVAL,
  SET_USERS_LOGGED_IN_TODAY,
  SET_USERS_REJECTED,
  SET_USERS_WITHOUT_SEPIA_INFO,
} from '../../store/users/mutations/mutationTypes';
import {
  GET_USERS,
  GET_USERS_PENDING_APPROVAL,
  GET_USERS_LOGGED_IN_TODAY,
  GET_USERS_REJECTED,
  GET_USERS_WITHOUT_SEPIA_INFO,
} from '../../store/users/getters/getterTypes';
import UserList from './UserList';

export default {
  name: 'users',
  props: {
    search: String,
  },
  components: {
    'user-list': UserList,
  },
  data() {
    return {
      activeTab: 0,
      isLoading: false,
      searchableFields: false,
      isSearchActive: false,
      filteredUsers: [],
      loadedTabs: new Set(),
      searchCache: new Map(),
      tabLoaders: {
        0: () => this.loadApprovedUsers(),
        1: () => this.loadPendingApprovalUsers(),
        2: () => this.loadLoggedInTodayUsers(),
        3: () => this.loadRejectedUsers(),
        4: () => this.loadInactiveUsers(),
      },
    };
  },

  watch: {
    search: {
      handler(newSearch) {
        if (newSearch) {
          this.debouncedSearch(newSearch);
        } else {
          this.clearSearch();
        }
      },
    },
  },

  created() {
    this.debouncedSearch = debounce((query) => {
      this.handleSearch(query);
    }, 500);
  },

  async mounted() {
    await this.loadAllUserData();
  },

  computed: {
    users() {
      return this.formatUserData(this.$store.getters[GET_USERS]);
    },
    usersPendingApproval() {
      return this.formatUserData(
        this.$store.getters[GET_USERS_PENDING_APPROVAL]
      );
    },
    usersLoggedInToday() {
      return this.formatUserData(
        this.$store.getters[GET_USERS_LOGGED_IN_TODAY]
      );
    },
    usersRejected() {
      return this.formatUserData(this.$store.getters[GET_USERS_REJECTED]);
    },
    usersWithoutSepiaInfo() {
      return this.formatUserData(
        this.$store.getters[GET_USERS_WITHOUT_SEPIA_INFO]
      );
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
    sites() {
      return this.$store.state.sites;
    },
    isCurrentUserPowerAdmin() {
      return (
        this.currentUser.role === 'admin' &&
        parseInt(this.currentUser.privil, 10) === 1
      );
    },
  },

  methods: {
    async loadAllUserData() {
      this.isLoading = true;
      try {
        const requests = [
          this.$store.dispatch(USERS_GET_USERS),
          this.$store.dispatch(USERS_GET_USERS_PENDING_APPROVAL),
          this.$store.dispatch(USERS_GET_USERS_LOGGED_IN_TODAY),
          this.$store.dispatch(USERS_GET_USERS_WITHOUT_SEPIA_INFO),
          this.$store.dispatch('getSites'),
        ];

        if (this.isCurrentUserPowerAdmin) {
          requests.push(this.$store.dispatch(USERS_GET_USERS_REJECTED));
        }

        await Promise.all(requests);

        // Mark all tabs as loaded
        [0, 1, 2, 3, 4].forEach((tab) => this.loadedTabs.add(tab));
      } catch (err) {
        console.error('Error loading users:', err);
      } finally {
        this.isLoading = false;
      }
    },

    async loadTabData(tabIndex) {
      const cacheKey = this.search
        ? `${tabIndex}-search-${this.search}`
        : tabIndex;

      if (this.loadedTabs.has(cacheKey)) {
        return;
      }

      const loader = this.tabLoaders[tabIndex];
      if (loader) {
        await loader();
        this.loadedTabs.add(cacheKey);
      }
    },

    async loadApprovedUsers(query = {}) {
      this.isLoading = true;
      try {
        await this.$store.dispatch(USERS_GET_USERS, {
          ...query,
          ...(this.search && { search: this.search }),
        });
      } catch (err) {
        console.error('Error loading approved users:', err);
      } finally {
        this.isLoading = false;
      }
    },

    async loadPendingApprovalUsers(query = {}) {
      this.isLoading = true;
      try {
        await this.$store.dispatch(USERS_GET_USERS_PENDING_APPROVAL, {
          ...query,
          ...(this.search && { search: this.search }),
        });
      } catch (err) {
        console.error('Error loading pending approval users:', err);
      } finally {
        this.isLoading = false;
      }
    },

    async loadLoggedInTodayUsers(query = {}) {
      this.isLoading = true;
      try {
        await this.$store.dispatch(USERS_GET_USERS_LOGGED_IN_TODAY, {
          ...query,
          ...(this.search && { search: this.search }),
        });
      } catch (err) {
        console.error('Error loading logged in today users:', err);
      } finally {
        this.isLoading = false;
      }
    },

    async loadRejectedUsers(query = {}) {
      if (!this.isCurrentUserPowerAdmin) return;

      this.isLoading = true;
      try {
        await this.$store.dispatch(USERS_GET_USERS_REJECTED, {
          ...query,
          ...(this.search && { search: this.search }),
        });
      } catch (err) {
        console.error('Error loading rejected users:', err);
      } finally {
        this.isLoading = false;
      }
    },

    async loadInactiveUsers(query = {}) {
      this.isLoading = true;
      try {
        await this.$store.dispatch(USERS_GET_USERS_WITHOUT_SEPIA_INFO, {
          ...query,
          ...(this.search && { search: this.search }),
        });
      } catch (err) {
        console.error('Error loading inactive users:', err);
      } finally {
        this.isLoading = false;
      }
    },

    async handleSearch(query) {
      // Check cache first
      if (this.searchCache.has(query)) {
        console.log('Using cached search results for:', query);
        const cachedData = this.searchCache.get(query);

        // Restore cached data to store
        this.$store.commit(SET_USERS, cachedData.users);
        this.$store.commit(
          SET_USERS_PENDING_APPROVAL,
          cachedData.usersPendingApproval
        );
        this.$store.commit(
          SET_USERS_LOGGED_IN_TODAY,
          cachedData.usersLoggedInToday
        );
        this.$store.commit(
          SET_USERS_WITHOUT_SEPIA_INFO,
          cachedData.usersWithoutSepiaInfo
        );

        if (this.isCurrentUserPowerAdmin && cachedData.usersRejected) {
          this.$store.commit(SET_USERS_REJECTED, cachedData.usersRejected);
        }

        this.isSearchActive = true;
        return;
      }

      this.loadedTabs.clear();
      this.isSearchActive = true;

      this.isLoading = true;
      try {
        const requests = [
          this.$store.dispatch(USERS_GET_USERS, {
            search: query,
          }),
          this.$store.dispatch(USERS_GET_USERS_PENDING_APPROVAL, {
            search: query,
          }),
          this.$store.dispatch(USERS_GET_USERS_LOGGED_IN_TODAY, {
            search: query,
          }),
          this.$store.dispatch(USERS_GET_USERS_WITHOUT_SEPIA_INFO, {
            search: query,
          }),
        ];

        if (this.isCurrentUserPowerAdmin) {
          requests.push(
            this.$store.dispatch(USERS_GET_USERS_REJECTED, {
              search: query,
            })
          );
        }

        await Promise.all(requests);

        // Cache the results (limit cache size to 20 entries)
        if (this.searchCache.size >= 20) {
          const firstKey = this.searchCache.keys().next().value;
          this.searchCache.delete(firstKey);
        }

        this.searchCache.set(query, {
          users: [...this.$store.getters[GET_USERS]],
          usersPendingApproval: [
            ...this.$store.getters[GET_USERS_PENDING_APPROVAL],
          ],
          usersLoggedInToday: [
            ...this.$store.getters[GET_USERS_LOGGED_IN_TODAY],
          ],
          usersWithoutSepiaInfo: [
            ...this.$store.getters[GET_USERS_WITHOUT_SEPIA_INFO],
          ],
          usersRejected: this.isCurrentUserPowerAdmin
            ? [...this.$store.getters[GET_USERS_REJECTED]]
            : null,
        });

        // Mark all tabs as loaded with search
        [0, 1, 2, 3, 4].forEach((tab) =>
          this.loadedTabs.add(`${tab}-search-${query}`)
        );
      } catch (err) {
        console.error('Error searching users:', err);
      } finally {
        this.isLoading = false;
      }
    },

    async clearSearch() {
      this.isSearchActive = false;
      if (this.debouncedSearch && this.debouncedSearch.cancel) {
        this.debouncedSearch.cancel();
      }
      await this.loadAllUserData();
    },

    beforeDestroy() {
      if (this.debouncedSearch && this.debouncedSearch.cancel) {
        this.debouncedSearch.cancel();
      }
    },

    editUserProfile(user) {
      this.$router.push({
        name: 'edit-user',
        params: { user, id: user._id },
      });
    },

    formatUserData(users) {
      return users.map((user) => {
        const siteFound = this.sites.find((site) => site._id === user.location);
        const formattedUser = { ...user };

        formattedUser.date = new Date(user.timestamp).toLocaleDateString();

        if (parseInt(user.privil, 10) === 1) {
          formattedUser.role = this.$t('edit_user_profile.power_admin_role');
        }

        formattedUser.location = siteFound ? siteFound.siteName : user.location;

        if (user.lastLoginDate) {
          formattedUser.lastLoginTimestamp = user.lastLoginDate;

          formattedUser.lastLoginFormatted = new Date(
            user.lastLoginDate
          ).toLocaleString(undefined, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });
        } else {
          formattedUser.lastLoginTimestamp = 0;
          formattedUser.lastLoginFormatted = this.$t('na');
        }

        return formattedUser;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.content {
  display: flex;
}
.user-list-tab {
  margin-top: 20px;
  margin-left: 20px;
}
.user-list {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  margin-left: 20px;
  margin-right: 20px;

  p {
    background-color: #f5f5f5;
    margin-bottom: 20px;
  }
}
</style>
